import React, { useState, useEffect } from "react";
import {
  Box,
  AccordionItem,
  AccordionButton,
  AccordionIcon,
  AccordionPanel,
  Heading,
  Input,
  useOutsideClick,
  useDisclosure,
  Text,
  UnorderedList,
  ListItem,
  Button,
  IconButton,
  Tooltip
} from "@chakra-ui/react";
import ReactDragListView from "react-drag-listview/lib/index.js";
import { EditIcon } from "../Icons/EditIcon";
import { DragDropIcon } from "../Icons/DragDropIcon";
import { DeleteIcon } from "../Icons/DeleteIcon";
import { DynoItem } from "../DynoItem";
import { ModalBox } from "../ModalBox";
import { AddIcon } from "../Icons/AddIcon";
import { AddFile } from "../Icons/AddFile";

export const Module = ({
  listId,
  listName,
  dynosList,
  isContentPage,
  editCallback,
  deleteCallback,
  modalTitle,
  deleteDynoCallback,
  updatDynoStatusCallback,
  updateDynoListCallback,
  isAdmin,
  publishStatus,
  onChangeLock,
  productType,
  openAddDynoModal,
  updatDynoAccessType,
  copyDynoCallback,
  splitDynoCallback,
  onMediaModalOpen,
}) => {
  const [isEditMode, setEditMode] = useState(false);
  const [listTitle, setListTitle] = useState(listName);
  const [indexedDynoList, setIndexedDynoList] = useState([]);
  const inputRef = React.useRef();
  useOutsideClick({
    ref: inputRef,
    handler: () => setEditMode(false),
  });
  const {
    isOpen: isDeleteModalOpen,
    onOpen: onDeleteModelOpen,
    onClose: onDeleteModelClose,
  } = useDisclosure();
  useEffect(() => {
    if (dynosList) {
      setIndexedDynoList(
        dynosList.sort((a, b) =>
          a.index < b.index ? -1 : a.index > b.index ? 1 : 0
        )
      );
    }
  }, [dynosList]);

  const updateListOrder = (fromIndex, toIndex) => {
    let data = [...indexedDynoList];
    data[toIndex] = { ...data[toIndex], index: fromIndex + 1 };
    data[fromIndex] = { ...data[fromIndex], index: toIndex + 1 };
    setIndexedDynoList(
      data.sort((a, b) => (a.index < b.index ? -1 : a.index > b.index ? 1 : 0))
    );
    updateDynoListCallback(data);
  };

  return (
    <AccordionItem
      borderRadius="10px"
      mb="20px"
      border="1px solid rgba(0, 0, 0, 0.32)"
    >
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        padding="10px"
      >
        <Heading
          as="h3"
          color="#424770"
          size="sm"
          isTruncated
          fontWeight="600"
          lineHeight="1.5"
          ml={3}
        >
          {isEditMode ? (
            <Input
              ref={inputRef}
              value={listTitle}
              onChange={(e) => {
                setListTitle(e.target.value);
                editCallback(e.target.value);
              }}
            />
          ) : (
            <>{listTitle || listName}</>
          )}
        </Heading>
        <Box display="flex" alignItems="center" justifyContent="flex-end">
          {isAdmin && !publishStatus && (
            <>
              <Tooltip label="Edit module name" hasArrow bg='gray.700'>
                <IconButton mr="10px" onClick={() => setEditMode(!isEditMode)} variant="ghost">
                  <EditIcon />
                </IconButton>
              </Tooltip>
              <Tooltip label="Add Media EdDoc" hasArrow bg='gray.700'>
                <IconButton mr="10px" onClick={onMediaModalOpen} variant="ghost">
                  <AddFile />
                </IconButton>
              </Tooltip>
              <Tooltip label="Add EdDoc to this module" hasArrow bg='gray.700'>
                <IconButton mr="10px" onClick={openAddDynoModal} variant="ghost">
                  <AddIcon />
                </IconButton>
              </Tooltip>
              <Tooltip label={`${indexedDynoList.length > 0 ? "Delete all the EdDoc first" : "Delete the module"}`} hasArrow bg='gray.700'>
                <IconButton mr="10px" onClick={onDeleteModelOpen} variant="ghost" disabled={indexedDynoList.length > 0}>
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            </>
          )}
          <AccordionButton w="auto" p="0" m="0">
            <AccordionIcon />
          </AccordionButton>
        </Box>
      </Box>
      <AccordionPanel p="0">
        <ReactDragListView
          nodeSelector="li.dyno-list-item"
          handleSelector="button.dyno-list-order-button"
          onDragEnd={(fromIndex, toIndex) =>
            updateListOrder(fromIndex, toIndex)
          }
        >
          <UnorderedList listStyleType="none" marginInlineStart="0">
            {indexedDynoList.map(({ id, index, dyno, isLocked }) => (
              <ListItem key={id} position="relative" className="dyno-list-item">
                {isAdmin && !publishStatus && (
                  <Button
                    className="dyno-list-order-button"
                    variant="ghost"
                    position="absolute"
                    top="12px"
                    left="-20px"
                    padding="0"
                    cursor="grab"
                    _hover={{
                      background: "none",
                    }}
                  >
                    <DragDropIcon />
                  </Button>
                )}
                <DynoItem
                  key={dyno.id}
                  dyno={dyno}
                  isContentPage={isContentPage}
                  modalTitle={modalTitle}
                  copyDynoCallback={() => copyDynoCallback(dyno)}
                  splitDynoCallback={() => splitDynoCallback(dyno, id, listId)}
                  deleteDynoCallback={() => deleteDynoCallback(id, dyno)}
                  updatDynoStatusCallback={updatDynoStatusCallback}
                  isAdmin={isAdmin}
                  publishStatus={publishStatus}
                  isLocked={isLocked}
                  dynosUnderListId={id}
                  listId={listId}
                  onChangeLock={onChangeLock}
                  productType={productType}
                  updatDynoAccessType={updatDynoAccessType}
                />
              </ListItem>
            ))}
          </UnorderedList>
        </ReactDragListView>
      </AccordionPanel>
      <ModalBox
        title={`Delete Module`}
        description={
          <Text fontSize="lg">
            Are you sure you want to delete {listName}.
          </Text>
        }
        isOpen={isDeleteModalOpen}
        onClose={onDeleteModelClose}
        isFooter={true}
        onCancel={onDeleteModelClose}
        onConfirm={deleteCallback}
        cancelButtonText="Cancel"
        confirmButtonText="Delete"
        isDeleteButton={true}
      ></ModalBox>
    </AccordionItem>
  );
};
